/**
 * wayai page — hub pages (wayai-pro/wayai#5333). This reads the invocation and writes the files; the
 * starter page is `lib/page-scaffold.ts`'s.
 *
 *   new <slug>  write a starter `pages/<slug>/page.yaml` and `index.html` into a hub folder
 *
 * Local only: no request, no sign-in. The hub folder is found by push's hub steps (`pageHubFolder`):
 * `--hub`, the folder the command runs in, the workspace's `default_hub`, or its only preview or new
 * hub folder; base folders and `default_base` play no part. Nothing is ever overwritten, and nothing
 * is written when anything is refused.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { parseArgs } from 'node:util';
import { PAGES_PER_HUB_MAX } from '@wayai/core/contracts/pages';
import { expected } from '../lib/expected.js';
import { wantsHelp } from '../lib/utils.js';
import { findGitRoot, resolvePushTarget, resolveWorkspaceDir, scanNewHubs, scanWorkspaceHubs } from '../lib/workspace.js';
import { hubsDirLabel, resolveLayout } from '../lib/layout.js';
import { readRepoDefaults } from '../lib/subtree-routing.js';
import { createFileNoFollow, isSymlink, isUnder, requireRealHubFolder, requireRealSubdirNoSymlink } from '../lib/fs-safety.js';
import { PAGES_DIR, PAGE_HTML_FILE, PAGE_MANIFEST_FILE } from '../lib/pages.js';
import { scaffoldPage } from '../lib/page-scaffold.js';

const USAGE = `
wayai page — a hub's pages

Usage:
  wayai page new <slug> [--audience team] [--title <title>] [--hub <uuid|folder-name>]

new: write a starter page, pages/<slug>/page.yaml and pages/<slug>/index.html, into a hub folder.
  The page is styled with WayAI's tokens and base classes, follows the viewer's theme and
  language, and shows loading, empty and error states. Name the tool it lists in both files,
  then \`wayai push\`. It never overwrites a file.

  <slug>       The page's id and folder name: lowercase letters, digits and hyphens.
  --audience   Who the page is for: team (the default; customer pages are not available yet).
  --title      What menus show (default: the slug in words).
  --hub        The hub folder: needed when the workspace has more than one,
               no default_hub, and you are not in one.
`;

function usageError(message: string): Error {
  return expected(`${message}\n${USAGE}`);
}

const OPTIONS = {
  audience: { type: 'string' },
  title: { type: 'string' },
  hub: { type: 'string' },
} as const;

export interface PageNewInvocation {
  slug: string;
  audience: string;
  title: string | undefined;
  hub: string | undefined;
}

/** The invocation, refused before anything is read or written when it is not one this command runs. */
export function parsePageArgs(args: string[]): PageNewInvocation {
  let parsed;
  try {
    parsed = parseArgs({ args, options: OPTIONS, allowPositionals: true, strict: true });
  } catch (err) {
    throw usageError(err instanceof Error ? err.message : String(err));
  }
  const [verb, slug, ...extra] = parsed.positionals;
  if (verb !== 'new') throw usageError(verb === undefined ? 'No subcommand given.' : `Unknown subcommand ${JSON.stringify(verb)}.`);
  if (slug === undefined) throw usageError('new <slug> needs a slug.');
  if (extra.length > 0) throw usageError(`Unexpected argument ${JSON.stringify(extra[0])}.`);
  const { audience, title, hub } = parsed.values;
  return { slug, audience: audience ?? 'team', title, hub };
}

/**
 * The page's hub folder, by push's hub steps: `--hub`, else the hub folder the command runs in, else the
 * workspace's `default_hub` (read, as push reads it, only from outside the hubs folder), else the only
 * preview or new hub folder. A page belongs to a hub, so base folders and `default_base` play no part.
 */
function pageHubFolder(hub: string | undefined): string {
  const gitRoot = findGitRoot();
  const fromDefault = hub === undefined && gitRoot !== null && !isUnder(resolveLayout(gitRoot).hubsDir, process.cwd())
    ? readRepoDefaults(gitRoot).default_hub
    : undefined;
  const selector = hub ?? fromDefault;
  const workspaceDir = resolveWorkspaceDir();
  const wsLabel = hubsDirLabel(gitRoot);
  const resolution = resolvePushTarget(workspaceDir, scanWorkspaceHubs(workspaceDir), scanNewHubs(workspaceDir), selector);
  if (!resolution.ok) {
    if (resolution.reason === 'selector_miss') throw expected(`No hub matching "${selector}" found in ${wsLabel}/.`);
    if (resolution.reason === 'ambiguous') {
      throw expected(`Several hub folders found in ${wsLabel}/. Pass --hub <uuid|folder-name>, or run from inside a hub folder.`);
    }
    throw expected(`No hub folder found in ${wsLabel}/. Pull a hub (\`wayai pull\`) or create one first.`);
  }
  const { target } = resolution;
  if (target.kind === 'existing' && target.hub.hubEnvironment === 'production') {
    throw expected(`${path.basename(target.hub.hubFolder)} is a read-only production mirror: add the page in its preview's folder, then publish.`);
  }
  return target.hub.hubFolder;
}

export async function pageCommand(args: string[]): Promise<void> {
  if (wantsHelp(args) || args.length === 0) {
    console.log(USAGE);
    return;
  }
  const invocation = parsePageArgs(args);
  // The platform's own rules first: nothing is resolved or written for a page push would refuse.
  const files = scaffoldPage(invocation);
  const hubFolder = pageHubFolder(invocation.hub);
  requireRealHubFolder(hubFolder, false);

  const rel = `${PAGES_DIR}/${invocation.slug}`;
  const pagesDir = path.join(hubFolder, PAGES_DIR);
  const dir = path.join(pagesDir, invocation.slug);
  // Both files are checked before either is written, so a refusal leaves nothing half-made.
  for (const file of [PAGE_MANIFEST_FILE, PAGE_HTML_FILE]) {
    if (fs.existsSync(path.join(dir, file)) || isSymlink(path.join(dir, file))) {
      throw expected(`${rel}/${file} is already there: \`wayai page new\` never overwrites a page. Pick another slug, or edit that page.`);
    }
  }
  if (pageFolderCount(pagesDir) >= PAGES_PER_HUB_MAX) {
    throw expected(`${path.basename(hubFolder)} already holds ${PAGES_PER_HUB_MAX} pages: a hub holds at most ${PAGES_PER_HUB_MAX} pages.`);
  }

  requireRealSubdirNoSymlink(hubFolder, dir, true);
  for (const [file, text] of [[PAGE_MANIFEST_FILE, files.manifestYaml], [PAGE_HTML_FILE, files.html]] as const) {
    // An exclusive create: a file that appeared since the check above is never written over.
    const outcome = createFileNoFollow(hubFolder, path.join(dir, file), Buffer.from(text));
    if (outcome !== 'created') throw expected(`${rel}/${file} could not be written (${outcome}): \`wayai page new\` never overwrites a page.`);
  }
  const shown = path.join(path.basename(hubFolder), rel);
  console.log(`Wrote ${shown}/${PAGE_MANIFEST_FILE} and ${shown}/${PAGE_HTML_FILE}.`);
  console.log('Next: add the tool it lists to page.yaml\'s tools and to LIST_TOOL in index.html, then `wayai push`.');
}

/** The page folders in `pagesDir` (a folder holding either page file), as push would count them. */
function pageFolderCount(pagesDir: string): number {
  let entries: fs.Dirent[];
  try {
    entries = fs.readdirSync(pagesDir, { withFileTypes: true });
  } catch {
    return 0;
  }
  return entries.filter((e) => e.isDirectory()
    && [PAGE_MANIFEST_FILE, PAGE_HTML_FILE].some((file) => fs.existsSync(path.join(pagesDir, e.name, file)))).length;
}
